import {useState} from 'react';
import {AlertTriangle} from 'lucide-react';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';

interface DeclineMedicalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

const CONFIRMATIONS = [
  'You were offered medical coverage and are voluntarily choosing not to enroll.',
  "You can't enroll again until the next open enrollment period unless you experience a qualifying life event (marriage, birth, loss of other coverage, etc.).",
  'Federal law requires us to document this declination. Your employer will be notified that you were offered coverage and declined.',
  'Going without medical coverage may have personal financial and health consequences.',
];

/**
 * Confirmation gate for declining medical coverage. Unlike skipping ancillary products, declining
 * medical carries MEC/ACA implications, so the user must explicitly acknowledge before it's recorded.
 */
export function DeclineMedicalDialog({open, onOpenChange, onConfirm}: DeclineMedicalDialogProps) {
  const [approved, setApproved] = useState(false);

  function handleOpenChange(next: boolean) {
    if (!next) setApproved(false);
    onOpenChange(next);
  }

  function handleConfirm() {
    if (!approved) return;
    onConfirm();
    setApproved(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`max-w-lg ${POSTHOG_UNMASK_CLASS}`}>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-[var(--enroll-ink)]'>
            <AlertTriangle className='h-5 w-5 text-amber-500' />
            Decline medical coverage?
          </DialogTitle>
        </DialogHeader>

        <DialogDescription className='text-sm text-neutral-600'>
          You are choosing not to enroll in any of your employer's medical plan options for the upcoming benefit
          year.
        </DialogDescription>

        <div className='space-y-2'>
          <p className='text-sm font-semibold text-neutral-900'>By declining, you confirm:</p>
          <ul className='space-y-2'>
            {CONFIRMATIONS.map((line) => (
              <li key={line} className='flex gap-2 text-sm text-neutral-600'>
                <span aria-hidden className='mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400' />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <label className='flex cursor-pointer items-start gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700'>
          <Checkbox
            checked={approved}
            onCheckedChange={(value) => setApproved(value === true)}
            className='mt-0.5'
          />
          <span>
            I approve this declination. I acknowledge that I was offered medical coverage by my employer and am
            declining it of my own free will.
          </span>
        </label>

        <div className='flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
          <button
            type='button'
            onClick={() => handleOpenChange(false)}
            className='inline-flex h-11 items-center justify-center rounded-lg border border-neutral-300 px-5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-100'
          >
            Go back
          </button>
          <button
            type='button'
            disabled={!approved}
            onClick={handleConfirm}
            className='inline-flex h-11 items-center justify-center rounded-lg bg-red-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40'
          >
            I approve my declination
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
